import { AnyToken } from 'src/common/types/models/Tokens'
import { big, normalize } from 'src/common/utils/big-utils'
import { allowance as allowanceERC20 } from 'thirdweb/extensions/erc20'
import { isApprovedForAll as isApprovedForAllERC721 } from 'thirdweb/extensions/erc721'
import { isApprovedForAll as isApprovedForAllERC1155 } from 'thirdweb/extensions/erc1155'
import { useReadContract } from 'thirdweb/react'
import useContract from '../common/useContract/useContract'
import { useMemo } from 'react'

type UseApprovalParams = {
  token: AnyToken | undefined
  spender: string | undefined
  owner: string | undefined
}

const useApproval = ({
  token,
  spender = '',
  owner = '',
}: UseApprovalParams) => {
  const { contract } = useContract(token?.contractAddress || '')

  const areParametersValid = owner && spender && token && token.contractAddress

  const isERC20 = Boolean(areParametersValid && token.type === 'ERC20')
  const isERC721 = Boolean(areParametersValid && token.type === 'ERC721')
  const isERC1155 = Boolean(areParametersValid && token.type === 'ERC1155')

  const {
    data: erc20Allowance,
    isLoading: isErc20Loading,
    error: erc20Error,
    refetch: refetchErc20,
  } = useReadContract(allowanceERC20, {
    contract,
    owner,
    spender,
    queryOptions: { enabled: isERC20 },
  })

  const {
    data: erc721Approval,
    isLoading: isErc721Loading,
    error: erc721Error,
    refetch: refetchErc721,
  } = useReadContract(isApprovedForAllERC721, {
    contract,
    owner,
    operator: spender,
    queryOptions: { enabled: isERC721 },
  })

  const {
    data: erc1155Approval,
    isLoading: isErc1155Loading,
    error: erc1155Error,
    refetch: refetchErc1155,
  } = useReadContract(isApprovedForAllERC1155, {
    contract,
    owner,
    operator: spender,
    queryOptions: { enabled: isERC1155 },
  })

  const allowance = useMemo(() => {
    if (!areParametersValid) return null
    const type = token.type
    switch (type) {
      case 'ERC20':
        return normalize(erc20Allowance?.toString() || '0', token.decimals)
      case 'ERC721':
      case 'ERC1155':
        // Both ERC721 and ERC1155 use boolean approvals for operators
        return big(erc721Approval || erc1155Approval ? '1' : '0')
      default:
        return null
    }
  }, [
    areParametersValid,
    erc20Allowance,
    erc721Approval,
    erc1155Approval,
    token?.decimals,
    token?.type,
  ])

  const refetch = () => {
    if (isERC20) return refetchErc20()
    if (isERC721) return refetchErc721()
    if (isERC1155) return refetchErc1155()
    return Promise.resolve()
  }

  const isLoading = isErc20Loading || isErc721Loading || isErc1155Loading
  const error = erc20Error || erc721Error || erc1155Error

  return { allowance, isLoading, error, refetch }
}

export default useApproval
